import InvoiceListNavigation from '../components/invoices/InvoiceListNavigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../utils/api';
import { formatCurrency, formatDate, downloadFile, previewFile, formatRecurrence, statusBadge } from '../utils/invoiceUtils';
import InvoicePayments from '../components/invoices/InvoicePayments';

const isOverdue = (inv) =>
  inv?.fecha_vencimiento &&
  ['Pendiente', 'Parcial', 'Validada'].includes(inv.estado) &&
  new Date(inv.fecha_vencimiento) < new Date(new Date().toDateString());

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  // Si venimos desde la ficha de un cliente (?clientId=), "volver" regresa a su pestaña Facturas.
  const backClientId = searchParams.get('clientId');
  const backTo = location.state?.invoiceNavigation?.returnTo || (backClientId ? `/clientes/${backClientId}?tab=Facturas` : '/facturas');
  const clientIdSuffix = backClientId ? `?clientId=${backClientId}` : '';
  const [invoice, setInvoice] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Error específico de la carga de líneas (la factura puede haberse cargado bien)
  const [linesError, setLinesError] = useState('');
  // Acción en curso para evitar dobles clics: 'pdf' | 'pdf-dolibarr' | 'rectificar' | 'facturar' | 'eliminar' | null
  const [acting, setActing] = useState(null);
  const loadController = useRef(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    loadController.current?.abort();
    const controller = new AbortController();
    loadController.current = controller;
    setLoading(true);
    setError('');
    setLinesError('');
    try {
      const [invRes, linesRes] = await Promise.all([
        apiFetch(`/api/invoices/${id}`, { headers: authHeaders, signal: controller.signal }),
        apiFetch(`/api/clients/invoices/${id}/lines`, { headers: authHeaders, signal: controller.signal })
      ]);
      if (invRes.status === 401) return handleUnauthorized();
      const invData = await parseJsonSafe(invRes);
      const linesData = await parseJsonSafe(linesRes);
      if (controller.signal.aborted) return;
      if (!invRes.ok) throw new Error(invData.message || 'Error cargando factura');
      setInvoice(invData);
      if (!linesRes.ok) {
        // No enmascarar el fallo como "Sin líneas": la factura puede tenerlas.
        setLines([]);
        setLinesError('No se pudieron cargar las líneas');
      } else {
        setLines(Array.isArray(linesData) ? linesData : (linesData?.lines || []));
      }
    } catch (err) {
      if (!controller.signal.aborted) setError(err.message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [id, authHeaders]);

  useEffect(() => { load(); return () => loadController.current?.abort(); }, [load]);

  const downloadPdf = async (endpoint, filename, key = 'pdf') => {
    if (acting) return;
    setActing(key);
    try { await downloadFile(endpoint, filename, token, handleUnauthorized); }
    catch (err) { alert(err.message); }
    finally { setActing(null); }
  };

  const previewPdf = async (endpoint, key = 'view') => {
    if (acting) return;
    setActing(key);
    try { await previewFile(endpoint, token, handleUnauthorized); }
    catch (err) { alert(err.message); }
    finally { setActing(null); }
  };

  const rectificar = async () => {
    if (acting) return;
    if (!window.confirm('¿Crear factura rectificativa?')) return;
    setActing('rectificar');
    try {
      const res = await apiFetch(`/api/clients/invoices/${id}/rectificar`, {
        method: 'POST', headers: authHeaders
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error');
      navigate(`/facturas/${data.id}`);
    } catch (err) { alert(err.message); }
    finally { setActing(null); }
  };

  const facturar = async () => {
    if (acting) return;
    if (!window.confirm('¿Crear factura normal desde esta proforma?')) return;
    setActing('facturar');
    try {
      const res = await apiFetch(`/api/clients/invoices/${id}/facturar`, {
        method: 'POST', headers: authHeaders
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error facturando proforma');
      navigate(`/facturas/${data.id}`);
    } catch (err) { alert(err.message); }
    finally { setActing(null); }
  };

  const emitir = async () => {
    if (acting) return;
    if (!window.confirm('¿Emitir esta factura? Se le asignará el número definitivo y pasará a Pendiente. NO se envía nada al cliente.')) return;
    setActing('emitir');
    try {
      const res = await apiFetch(`/api/clients/invoices/${id}/emit`, {
        method: 'POST', headers: authHeaders
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error emitiendo la factura');
      load();
    } catch (err) { alert(err.message); }
    finally { setActing(null); }
  };

  const toggleRecurrentPaused = async () => {
    if (acting) return;
    const paused = !invoice.recurrent_paused;
    setActing('recurrent-paused');
    try {
      const res = await apiFetch(`/api/clients/invoices/${id}/recurrent-paused`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ paused })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      load();
    } catch (err) { alert(err.message); }
    finally { setActing(null); }
  };

  const anular = async () => {
    if (acting) return;
    const motivo = window.prompt('Motivo de la anulación (opcional):', '');
    if (motivo === null) return; // cancelado
    setActing('anular');
    try {
      const res = await apiFetch(`/api/clients/invoices/${id}/anular`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ motivo })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo anular');
      load();
    } catch (err) { alert(err.message); }
    finally { setActing(null); }
  };

  const eliminar = async () => {
    if (acting) return;
    if (!window.confirm('¿Eliminar esta factura?')) return;
    setActing('eliminar');
    try {
      const res = await apiFetch(`/api/clients/invoices/${id}`, {
        method: 'DELETE', headers: authHeaders
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        throw new Error(data.message || 'Error');
      }
      navigate('/facturas');
    } catch (err) { alert(err.message); }
    finally { setActing(null); }
  };

  if (loading) return <div className="container py-4">Cargando…</div>;
  if (error) return <div className="container py-4"><div className="alert alert-danger">{error}</div></div>;
  if (!invoice) return null;

  const overdue = isOverdue(invoice);
  const isProforma = invoice.invoice_type === 'proforma';
  const isRectificativa = invoice.invoice_type === 'rectificativa';
  const isFromDolibarr = Boolean(invoice.dolibarr_invoice_id);
  const isRecurrent = Boolean(invoice.is_recurrent);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <div className="text-muted small">
            <Link to={backTo} state={location.state}>← {backClientId ? 'Volver al cliente' : 'Facturas'}</Link>
          </div>
          <h3 className="mb-1">
            {invoice.numero || `Factura #${invoice.id}`}
            {isProforma && <span className="badge bg-info text-dark ms-2">Proforma</span>}
            {isRectificativa && <span className="badge bg-warning text-dark ms-2">Rectificativa</span>}
            {isFromDolibarr && <span className="badge bg-primary ms-2">Dolibarr</span>}
            {isRecurrent && <span className="badge ms-2" style={{ backgroundColor: '#50BAFC', color: '#053366' }}>Recurrente</span>}
            {overdue && <span className="badge bg-danger ms-2">Vencida</span>}
          </h3>
          <div className="text-muted">
            {invoice.client_id ? (
              <Link to={`/clientes/${invoice.client_id}`}>{invoice.razon_social || `Cliente #${invoice.client_id}`}</Link>
            ) : (invoice.razon_social || '-')}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <InvoiceListNavigation id={id} suffix={clientIdSuffix} />
          {!isRecurrent && (
            <button
              className="btn btn-primary btn-sm"
              disabled={Boolean(acting)}
              onClick={() => previewPdf(`/api/clients/invoices/${id}/pdf`, 'view')}
            >
              {acting === 'view' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Abriendo…</>
              ) : 'Ver PDF'}
            </button>
          )}
          {!isRecurrent && (
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={Boolean(acting)}
              onClick={() => downloadPdf(`/api/clients/invoices/${id}/pdf`, `${invoice.numero || 'factura'}.pdf`, 'pdf')}
            >
              {acting === 'pdf' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>PDF…</>
              ) : 'Descargar PDF'}
            </button>
          )}
          {isFromDolibarr && !isRecurrent && (
            <button
              className="btn btn-primary btn-sm"
              disabled={Boolean(acting)}
              onClick={() => previewPdf(`/api/clients/invoices/${id}/dolibarr-pdf`, 'view-dolibarr')}
            >
              {acting === 'view-dolibarr' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Abriendo…</>
              ) : 'Ver PDF Dolibarr'}
            </button>
          )}
          {isFromDolibarr && !isRecurrent && (
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={Boolean(acting)}
              onClick={() => downloadPdf(`/api/clients/invoices/${id}/dolibarr-pdf`, `${invoice.numero || 'factura'}.pdf`, 'pdf-dolibarr')}
            >
              {acting === 'pdf-dolibarr' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>PDF Dolibarr…</>
              ) : 'Descargar PDF Dolibarr'}
            </button>
          )}
          {!isFromDolibarr && !isRecurrent && !isProforma && !isRectificativa && String(invoice.estado || '').toLowerCase() === 'borrador' && (
            <button
              className="btn btn-success btn-sm"
              disabled={Boolean(acting)}
              onClick={emitir}
              title="Asigna el número definitivo y pasa la factura a Pendiente. No envía nada al cliente."
            >
              {acting === 'emitir' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Emitiendo…</>
              ) : 'Emitir'}
            </button>
          )}
          {!isFromDolibarr && !(isProforma && invoice.estado === 'Facturada') && (
            <button className="btn btn-outline-secondary btn-sm" disabled={Boolean(acting)} onClick={() => navigate(`/facturas/${id}/editar${clientIdSuffix}`)}>
              Editar
            </button>
          )}
          {isProforma && !isFromDolibarr && invoice.estado !== 'Facturada' && (
            <button className="btn btn-outline-success btn-sm" disabled={Boolean(acting)} onClick={facturar}>
              {acting === 'facturar' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Facturar…</>
              ) : 'Facturar'}
            </button>
          )}
          {!isProforma && !isRectificativa && !isFromDolibarr && !isRecurrent && invoice.estado !== 'Borrador' && (
            <button className="btn btn-outline-warning btn-sm" disabled={Boolean(acting)} onClick={rectificar}>
              {acting === 'rectificar' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Rectificar…</>
              ) : 'Rectificar'}
            </button>
          )}
          {/* Anular: factura/rectificativa local emitida y NO cobrada (una cobrada se
              rectifica). No borrador, no ya anulada, no pagada/parcial/devuelta. */}
          {!isFromDolibarr && !isRecurrent && !isProforma && !['borrador', 'anulada', 'pagada', 'parcial', 'devuelta'].includes(String(invoice.estado || '').toLowerCase()) && (
            <button className="btn btn-outline-dark btn-sm" disabled={Boolean(acting)} onClick={anular}>
              {acting === 'anular' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Anulando…</>
              ) : 'Anular'}
            </button>
          )}
          {/* Eliminar: solo plantillas recurrentes y borradores/proformas locales. Una
              rectificativa/factura emitida NO se borra (deja hueco fiscal): se Anula. */}
          {!isFromDolibarr && (isRecurrent || ['borrador', 'proforma'].includes(String(invoice.estado || '').toLowerCase())) && (
            <button className="btn btn-outline-danger btn-sm" disabled={Boolean(acting)} onClick={eliminar}>
              {acting === 'eliminar' ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Eliminar…</>
              ) : 'Eliminar'}
            </button>
          )}
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <div className="card"><div className="card-body py-2">
            <div className="text-muted small">{isRecurrent ? 'Activación' : 'Estado'}</div>
            {isRecurrent ? (
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${invoice.recurrent_paused ? 'bg-secondary' : 'bg-success'}`}>
                  {invoice.recurrent_paused ? 'Desactivada' : 'Activa'}
                </span>
                <button className="btn btn-sm btn-link p-0" disabled={Boolean(acting)} onClick={toggleRecurrentPaused}>
                  {invoice.recurrent_paused ? 'Activar' : 'Desactivar'}
                </button>
              </div>
            ) : (
              <span className={`badge ${statusBadge(invoice.estado)}`}>{invoice.estado || '-'}</span>
            )}
          </div></div>
        </div>
        <div className="col-md-3">
          <div className="card"><div className="card-body py-2">
            <div className="text-muted small">Fecha emisión</div>
            <div>{formatDate(invoice.fecha)}</div>
          </div></div>
        </div>
        <div className="col-md-3">
          <div className="card"><div className="card-body py-2">
            <div className="text-muted small">Vencimiento</div>
            <div className={overdue ? 'text-danger fw-bold' : ''}>
              {invoice.fecha_vencimiento ? formatDate(invoice.fecha_vencimiento) : '-'}
            </div>
          </div></div>
        </div>
        <div className="col-md-3">
          <div className="card"><div className="card-body py-2">
            <div className="text-muted small">Total</div>
            <div className="h5 mb-0">{formatCurrency(invoice.total)}</div>
          </div></div>
        </div>
      </div>

      {isFromDolibarr && (
        <div className="alert alert-primary py-2">
          Factura sincronizada desde Dolibarr (fuente de verdad). Los datos, líneas y rectificativas deben gestionarse en Dolibarr. Aquí puedes registrar pagos y se propagarán automáticamente.
        </div>
      )}
      {isRecurrent && (
        <div className="alert alert-info py-2">
          Factura recurrente · Periodicidad: {formatRecurrence(invoice.recurrent_unit, invoice.recurrent_interval)}{invoice.recurrent_next_date ? ` · Próxima emisión: ${formatDate(invoice.recurrent_next_date)}` : ''}. Es una plantilla que genera facturas automáticamente; no es una factura fiscal.
        </div>
      )}
      {invoice.rectifica_invoice_id && (
        <div className="alert alert-warning py-2">
          Rectifica a la factura{' '}
          <Link to={`/facturas/${invoice.rectifica_invoice_id}`}>#{invoice.rectifica_invoice_id}</Link>
        </div>
      )}
      {invoice.origin_invoice_id && !invoice.rectifica_invoice_id && (
        <div className="alert alert-info py-2">
          {invoice.origin_is_recurrent ? 'Generada desde la recurrente ' : 'Generada desde la proforma '}
          <Link to={`/facturas/${invoice.origin_invoice_id}`}>#{invoice.origin_invoice_id}</Link>
        </div>
      )}
      {isProforma && invoice.generated_invoice_id && (
        <div className="alert alert-success py-2">
          Proforma facturada · factura generada{' '}
          <Link to={`/facturas/${invoice.generated_invoice_id}`}>#{invoice.generated_invoice_id}</Link>
        </div>
      )}

      <div className="card mb-3">
        <div className="card-header"><strong>Líneas</strong></div>
        <div className="card-body p-0">
          {linesError ? (
            <div className="alert alert-warning m-3">{linesError}</div>
          ) : lines.length === 0 ? (
            <div className="p-3 text-muted">Sin líneas.</div>
          ) : (
            <table className="table mb-0">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th className="text-end">Cantidad</th>
                  <th className="text-end">Precio</th>
                  <th className="text-end">IVA</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((ln) => (
                  <tr key={ln.id}>
                    <td>
                      {ln.concepto}
                      {(ln.period_start || ln.period_end) && (
                        <div className="text-muted small">
                          {formatDate(ln.period_start)} — {formatDate(ln.period_end)}
                        </div>
                      )}
                    </td>
                    <td className="text-end">{ln.cantidad}</td>
                    <td className="text-end">{formatCurrency(ln.precio)}</td>
                    <td className="text-end">{ln.tva_tx != null ? `${Number(ln.tva_tx)}%` : '21%'}</td>
                    <td className="text-end">{formatCurrency(ln.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" className="text-end text-muted">Subtotal</td>
                  <td className="text-end">{formatCurrency(invoice.subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan="4" className="text-end text-muted">IVA</td>
                  <td className="text-end">{formatCurrency(invoice.iva)}</td>
                </tr>
                <tr>
                  <td colSpan="4" className="text-end fw-bold">Total</td>
                  <td className="text-end fw-bold">{formatCurrency(invoice.total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {!isProforma && !isRecurrent && !isRectificativa && (
        <InvoicePayments
          invoiceId={Number(id)}
          invoiceTotal={invoice.total}
          dolibarrLinked={isFromDolibarr}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default InvoiceDetailPage;
